import { useState, useEffect } from "react";
import { ArrowRight, CaretDown, X } from "@phosphor-icons/react";

/**
 * Visual flow strip used across the app to make multi-step processes scannable
 * at a glance — particularly helpful for users who process visual cues faster
 * than walls of text. Each step is a colored card with a number, an icon, a
 * short title, and a one-line subtitle. Arrows separate steps on desktop;
 * cards stack vertically on mobile.
 *
 * Usage:
 *   <FlowStrip
 *     storageKey="flow-remittances-v1"   // optional — enables dismiss-and-remember
 *     title="The remittance flow at a glance"
 *     steps={[
 *       { icon: Camera, title: "Upload", sub: "Photo or PDF",   accent: "#002FA7" },
 *       { icon: Sparkle, title: "Auto-fill", sub: "Read fields", accent: "#7C3AED" },
 *       ...
 *     ]}
 *     footer={<>Tip text here…</>}
 *   />
 */
export default function FlowStrip({
  storageKey,
  title,
  steps,
  footer,
  testId = "flow-strip",
  defaultCollapsed = false,
}) {
  const [open, setOpen] = useState(!defaultCollapsed);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!storageKey) return;
    try {
      if (localStorage.getItem(storageKey) === "1") {
        setDismissed(true);
      }
    } catch (e) { /* ignore */ }
  }, [storageKey]);

  if (dismissed) return null;

  const dismiss = () => {
    if (storageKey) {
      try { localStorage.setItem(storageKey, "1"); } catch (e) { /* ignore */ }
    }
    setDismissed(true);
  };

  return (
    <section
      className="surface-card bg-white mb-6 border-l-2 border-[#002FA7]"
      data-testid={testId}
    >
      <div className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-[#FAFAF7]">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex-1 flex items-center gap-2 text-left"
          data-testid={`${testId}-toggle`}
        >
          <div className="text-[11px] uppercase tracking-wider text-[#002FA7] font-semibold">
            {title}
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {storageKey && (
            <button
              type="button"
              onClick={dismiss}
              className="p-1 text-[#A3A3A3] hover:text-[#0A0A0A]"
              aria-label="Hide this guide"
              title="Hide this guide (you can show it again from Help)"
              data-testid={`${testId}-dismiss`}
            >
              <X size={12} weight="bold" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="p-1"
            aria-label={open ? "Collapse" : "Expand"}
          >
            <CaretDown
              size={14}
              weight="bold"
              className={`text-[#525252] transition-transform ${open ? "" : "-rotate-90"}`}
            />
          </button>
        </div>
      </div>

      {open && (
        <div className="px-4 pb-4 pt-0">
          <div className="flex flex-col md:flex-row gap-2 md:gap-1">
            {steps.map((s, i) => (
              <FlowStepRow key={s.title || `step-${i}`} n={i + 1} {...s} />
            )).reduce((acc, el, i) => {
              if (i === 0) return [el];
              return [...acc, <FlowArrow key={`arrow-after-${steps[i - 1]?.title || i}`} />, el];
            }, [])}
          </div>
          {footer && (
            <div className="text-[11px] text-[#525252] italic mt-2 leading-relaxed">
              {footer}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function FlowStepRow({ n, icon: Icon, title, sub, accent = "#002FA7" }) {
  return (
    <div className="flex-1 min-w-[140px]" data-testid={`flow-step-${n}`}>
      <div className="bg-white border-2 p-3 h-full" style={{ borderColor: accent }}>
        <div className="flex items-center gap-2 mb-1.5">
          <span
            className="w-5 h-5 flex items-center justify-center text-[10px] font-bold text-white rounded-full"
            style={{ backgroundColor: accent }}
          >{n}</span>
          {Icon && <Icon size={14} weight="duotone" style={{ color: accent }} />}
        </div>
        <div className="font-heading font-semibold text-[12px] leading-tight mb-1">{title}</div>
        <div className="text-[11px] text-[#525252] leading-snug">{sub}</div>
      </div>
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="hidden md:flex items-center justify-center text-[#A3A3A3] flex-shrink-0">
      <ArrowRight size={16} weight="bold" />
    </div>
  );
}
